import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Flex,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import {
  IconEdit,
  IconPackage,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useEffect, useMemo, useState } from 'react';
import { EpiTutorial } from '../epi-tutorial';
import { useEpiCatalog } from '../../data/hooks/employee';
import { Epi, EPI_CONTROL_TYPE_OPTIONS } from '../../models/employees';
import { successNotification } from '../../providers/mantine-notifications';
import { SECTOR_OPTIONS } from '../../utils/constants/sectors';

const EPI_CATEGORIES = [
  'Proteção da cabeça',
  'Proteção dos olhos e face',
  'Proteção auditiva',
  'Proteção respiratória',
  'Proteção do tronco',
  'Proteção dos membros superiores',
  'Proteção dos membros inferiores',
  'Proteção do corpo inteiro',
  'Proteção contra quedas',
  'Outro',
];

type FormValues = {
  name: string;
  controlType: string;
  category: string;
  brand: string;
  ca: string;
  caExpirationDate: Date | null;
  supplier: string;
  size: string;
  stock: number | '';
  minimumStock: number | '';
  replacementPeriodDays: number | '';
  sectors: string[];
  isActive: boolean;
};

const emptyForm: FormValues = {
  name: '',
  controlType: 'reutilizavel',
  category: '',
  brand: '',
  ca: '',
  caExpirationDate: null,
  supplier: '',
  size: '',
  stock: '',
  minimumStock: '',
  replacementPeriodDays: '',
  sectors: [],
  isActive: true,
};

function epiToForm(epi: Epi): FormValues {
  return {
    name: epi.name ?? '',
    controlType: epi.controlType ?? 'reutilizavel',
    category: epi.category ?? '',
    brand: epi.brand ?? '',
    ca: epi.ca ?? '',
    caExpirationDate: epi.caExpirationDate
      ? new Date(epi.caExpirationDate)
      : null,
    supplier: epi.supplier ?? '',
    size: epi.size ?? '',
    stock: epi.stock ?? '',
    minimumStock: epi.minimumStock ?? '',
    replacementPeriodDays: epi.replacementPeriodDays ?? '',
    sectors: epi.sectors
      ? epi.sectors
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    isActive: epi.isActive ?? true,
  };
}

function formToPayload(v: FormValues) {
  return {
    name: v.name,
    controlType: v.controlType || 'reutilizavel',
    category: v.category || undefined,
    brand: v.brand || undefined,
    ca: v.ca || undefined,
    caExpirationDate: v.caExpirationDate
      ? v.caExpirationDate.toISOString()
      : undefined,
    supplier: v.supplier || undefined,
    size: v.size || undefined,
    stock: v.stock !== '' ? Number(v.stock) : undefined,
    minimumStock: v.minimumStock !== '' ? Number(v.minimumStock) : undefined,
    replacementPeriodDays:
      v.replacementPeriodDays !== ''
        ? Number(v.replacementPeriodDays)
        : undefined,
    sectors: v.sectors.length > 0 ? v.sectors.join(', ') : undefined,
    isActive: v.isActive,
  };
}

interface EpiCatalogPanelProps {
  onSaved?: () => void;
}

export function EpiCatalogPanel({ onSaved }: EpiCatalogPanelProps) {
  const {
    list: listCatalog,
    catalog,
    create,
    update,
    remove,
    loading,
  } = useEpiCatalog();

  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedEpi, setSelectedEpi] = useState<Epi | null>(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);

  const form = useForm<FormValues>({ initialValues: emptyForm });

  useEffect(() => {
    listCatalog();
  }, []);

  const filtered = useMemo(() => {
    if (!catalog) return [];
    return catalog.filter((epi) => {
      const matchSearch =
        !search ||
        epi.name.toLowerCase().includes(search.toLowerCase()) ||
        (epi.brand ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (epi.ca ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (epi.supplier ?? '').toLowerCase().includes(search.toLowerCase());
      const matchCategory = !filterCategory || epi.category === filterCategory;
      const matchStatus =
        !filterStatus ||
        (filterStatus === 'ativo' ? epi.isActive : !epi.isActive);
      return matchSearch && matchCategory && matchStatus;
    });
  }, [catalog, search, filterCategory, filterStatus]);

  function openNew() {
    setSelectedEpi(null);
    form.setValues(emptyForm);
    setDrawerOpened(true);
  }

  function openEdit(epi: Epi) {
    setSelectedEpi(epi);
    form.setValues(epiToForm(epi));
    setDrawerOpened(true);
  }

  function closeDrawer() {
    setDrawerOpened(false);
    form.reset();
  }

  async function handleSubmit(values: FormValues) {
    const payload = formToPayload(values);
    if (selectedEpi) {
      await update(selectedEpi.id, payload, () => {
        successNotification({
          title: 'Sucesso',
          message: 'EPI atualizado no catálogo',
        });
        closeDrawer();
        listCatalog();
        onSaved?.();
      });
    } else {
      await create(payload, () => {
        successNotification({
          title: 'Sucesso',
          message: 'EPI adicionado ao catálogo',
        });
        closeDrawer();
        listCatalog();
        onSaved?.();
      });
    }
  }

  async function handleDelete(id: number) {
    if (window.confirm('Deseja realmente excluir este EPI do catálogo?')) {
      await remove(id, () => {
        successNotification({
          title: 'Sucesso',
          message: 'EPI removido do catálogo',
        });
        listCatalog();
        onSaved?.();
      });
    }
  }

  const stockBadge = (epi: Epi) => {
    if (epi.stock == null) return <Text color="dimmed">—</Text>;
    const low = epi.minimumStock != null && epi.stock <= epi.minimumStock;
    return (
      <Tooltip
        label={low ? 'Estoque abaixo do mínimo' : 'Estoque OK'}
        withArrow
        disabled={epi.minimumStock == null}
      >
        <Text color={low ? 'red' : 'green'} weight={600}>
          {epi.stock}
          {epi.minimumStock != null && (
            <Text span color="dimmed" weight={400} size="xs">
              {' '}
              / mín {epi.minimumStock}
            </Text>
          )}
        </Text>
      </Tooltip>
    );
  };

  const caExpiration = (epi: Epi) => {
    if (!epi.caExpirationDate) return <Text color="dimmed">—</Text>;
    const date = new Date(epi.caExpirationDate);
    const expired = date < new Date();
    return (
      <Text
        color={expired ? 'red' : undefined}
        weight={expired ? 600 : undefined}
      >
        {date.toLocaleDateString('pt-BR')}
        {expired && (
          <Text span size="xs" color="red">
            {' '}
            (vencido)
          </Text>
        )}
      </Text>
    );
  };

  return (
    <>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Group spacing={10}>
          <IconPackage size={24} />
          <Text fw={700} size="lg">
            Catálogo de EPIs
          </Text>
        </Group>
        <Group spacing={8}>
          <EpiTutorial screen="catalog" />
          <Button leftIcon={<IconPlus size={16} />} onClick={openNew}>
            Novo EPI
          </Button>
        </Group>
      </Flex>
      <Text color="dimmed" mb={16} size="sm">
        Gerencie os tipos de EPIs disponíveis para entrega, estoque e validade
        de CA.
      </Text>

      {/* Filtros */}
      <Flex gap={12} mb={16} wrap="wrap">
        <TextInput
          placeholder="Buscar por nome, marca, CA, fornecedor..."
          icon={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          rightSection={
            search ? (
              <ActionIcon size="xs" onClick={() => setSearch('')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null
          }
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select
          placeholder="Categoria"
          data={EPI_CATEGORIES}
          value={filterCategory}
          onChange={setFilterCategory}
          clearable
          style={{ minWidth: 180 }}
        />
        <Select
          placeholder="Status"
          data={[
            { value: 'ativo', label: 'Ativo' },
            { value: 'inativo', label: 'Inativo' },
          ]}
          value={filterStatus}
          onChange={setFilterStatus}
          clearable
          style={{ minWidth: 120 }}
        />
      </Flex>

      {/* Tabela */}
      <Box sx={{ overflow: 'auto' }}>
        <DataTable
          withBorder
          borderRadius="sm"
          striped
          highlightOnHover
          records={filtered}
          fetching={loading}
          noRecordsText="Nenhum EPI encontrado"
          minHeight={200}
          columns={[
            {
              accessor: 'name',
              title: 'Nome do EPI',
              width: 200,
              ellipsis: true,
            },
            {
              accessor: 'category',
              title: 'Categoria',
              width: 180,
              render: (r) =>
                r.category ? (
                  <Badge variant="outline" color="blue" size="sm">
                    {r.category}
                  </Badge>
                ) : (
                  <Text color="dimmed">—</Text>
                ),
            },
            {
              accessor: 'brand',
              title: 'Marca/Modelo',
              width: 150,
              render: (r) => r.brand ?? <Text color="dimmed">—</Text>,
            },
            {
              accessor: 'ca',
              title: 'CA',
              width: 90,
              render: (r) => r.ca ?? <Text color="dimmed">—</Text>,
            },
            {
              accessor: 'caExpirationDate',
              title: 'Validade CA',
              width: 130,
              render: (r) => caExpiration(r),
            },
            {
              accessor: 'supplier',
              title: 'Fornecedor',
              width: 160,
              render: (r) => r.supplier ?? <Text color="dimmed">—</Text>,
            },
            {
              accessor: 'size',
              title: 'Tamanho',
              width: 90,
              render: (r) => r.size ?? <Text color="dimmed">—</Text>,
            },
            {
              accessor: 'stock',
              title: 'Estoque',
              width: 120,
              render: (r) => stockBadge(r),
            },
            {
              accessor: 'controlType',
              title: 'Controle',
              width: 140,
              render: (r) => {
                const map: Record<string, { color: string; label: string }> = {
                  descartavel: { color: 'orange', label: 'Descartável' },
                  reutilizavel: { color: 'blue', label: 'Reutilizável' },
                  por_condicao: { color: 'violet', label: 'Por condição' },
                };
                const entry = r.controlType ? map[r.controlType] : null;
                return entry ? (
                  <Badge color={entry.color} variant="light" size="sm">
                    {entry.label}
                  </Badge>
                ) : (
                  <Badge color="blue" variant="light" size="sm">
                    Reutilizável
                  </Badge>
                );
              },
            },
            {
              accessor: 'replacementPeriodDays',
              title: 'Troca (dias)',
              width: 110,
              render: (r) =>
                r.replacementPeriodDays != null ? (
                  `${r.replacementPeriodDays}d`
                ) : (
                  <Text color="dimmed">—</Text>
                ),
            },
            {
              accessor: 'sectors',
              title: 'Setores/Cargos',
              width: 200,
              ellipsis: true,
              render: (r) => r.sectors ?? <Text color="dimmed">—</Text>,
            },
            {
              accessor: 'isActive',
              title: 'Status',
              width: 90,
              render: (r) => (
                <Badge
                  color={r.isActive ? 'green' : 'gray'}
                  variant="filled"
                  size="sm"
                >
                  {r.isActive ? 'Ativo' : 'Inativo'}
                </Badge>
              ),
            },
            {
              accessor: 'actions',
              title: '',
              width: 80,
              textAlignment: 'right',
              render: (record) => (
                <Group spacing={4} position="right" noWrap>
                  <ActionIcon
                    color="blue"
                    variant="light"
                    onClick={() => openEdit(record)}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    color="red"
                    variant="light"
                    onClick={() => handleDelete(record.id)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ),
            },
          ]}
        />
      </Box>

      {/* Drawer de cadastro/edição */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={
          <Group spacing={8}>
            <IconPackage size={20} />
            <Text weight={600}>{selectedEpi ? 'Editar EPI' : 'Novo EPI'}</Text>
          </Group>
        }
        position="right"
        size={480}
        padding={0}
        styles={{
          drawer: { display: 'flex', flexDirection: 'column' },
          header: {
            padding: '20px 24px',
            borderBottom: '1px solid #e9ecef',
            marginBottom: 0,
          },
          body: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: 0,
          },
        }}
      >
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px 24px 8px' }}>
            <Stack spacing={12}>
              <Text size="xs" color="dimmed" weight={600} transform="uppercase">
                Identificação
              </Text>
              <TextInput
                required
                label="Nome do EPI"
                placeholder="ex: Luva Térmica"
                {...form.getInputProps('name')}
              />
              <Select
                label="Tipo/Categoria"
                placeholder="Selecione uma categoria"
                data={EPI_CATEGORIES}
                clearable
                {...form.getInputProps('category')}
              />
              <TextInput
                label="Marca/Modelo"
                placeholder="ex: 3M Modelo XR-200"
                {...form.getInputProps('brand')}
              />
              <TextInput
                label="Tamanho (quando aplicável)"
                placeholder="ex: P, M, G, 39, 40..."
                {...form.getInputProps('size')}
              />

              <Divider mt={4} />
              <Text size="xs" color="dimmed" weight={600} transform="uppercase">
                Certificação
              </Text>
              <Flex gap={12}>
                <TextInput
                  label="CA (Certificado de Aprovação)"
                  placeholder="ex: 12345"
                  style={{ flex: 1 }}
                  {...form.getInputProps('ca')}
                />
                <DatePicker
                  label="Validade do CA"
                  placeholder="DD/MM/AAAA"
                  locale="pt-br"
                  inputFormat="DD/MM/YYYY"
                  style={{ flex: 1 }}
                  {...form.getInputProps('caExpirationDate')}
                />
              </Flex>

              <Divider mt={4} />
              <Text size="xs" color="dimmed" weight={600} transform="uppercase">
                Estoque e Fornecimento
              </Text>
              <TextInput
                label="Fornecedor"
                placeholder="ex: Distribuidora ABC"
                {...form.getInputProps('supplier')}
              />
              <Flex gap={12}>
                <NumberInput
                  label="Quantidade em estoque"
                  placeholder="0"
                  min={0}
                  style={{ flex: 1 }}
                  {...form.getInputProps('stock')}
                />
                <NumberInput
                  label="Estoque mínimo"
                  placeholder="0"
                  min={0}
                  style={{ flex: 1 }}
                  {...form.getInputProps('minimumStock')}
                />
              </Flex>
              <Select
                required
                label="Tipo de controle"
                description="Define como o sistema rastreia este EPI"
                data={EPI_CONTROL_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
                {...form.getInputProps('controlType')}
              />
              {form.values.controlType === 'reutilizavel' && (
                <NumberInput
                  label="Periodicidade de troca (dias)"
                  placeholder="ex: 180"
                  min={1}
                  {...form.getInputProps('replacementPeriodDays')}
                />
              )}

              <Divider mt={4} />
              <Text size="xs" color="dimmed" weight={600} transform="uppercase">
                Uso e Status
              </Text>
              <MultiSelect
                label="Setores/Cargos que utilizam"
                placeholder="Selecione os setores"
                data={SECTOR_OPTIONS}
                searchable
                creatable
                getCreateLabel={(q) => `+ Adicionar "${q}"`}
                onCreate={(q) => ({ value: q, label: q })}
                {...form.getInputProps('sectors')}
              />
              <Switch
                label="EPI ativo"
                description="EPIs inativos não aparecem para vinculação com funcionários"
                checked={form.values.isActive}
                onChange={(e) =>
                  form.setFieldValue('isActive', e.currentTarget.checked)
                }
              />
            </Stack>
          </div>

          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #e9ecef',
              background: 'white',
            }}
          >
            <Flex gap={8}>
              <Button
                variant="default"
                onClick={closeDrawer}
                style={{ flex: 1 }}
              >
                Cancelar
              </Button>
              <Button type="submit" loading={loading} style={{ flex: 1 }}>
                Salvar
              </Button>
            </Flex>
          </div>
        </form>
      </Drawer>
    </>
  );
}
